import {
  ApiACLRole,
  ApiClient,
  ApiOnboardingStep,
  ApiPlatform,
  ApiProject,
  ApiProjectSetting,
  ApiProjectSettingKey,
  ApiProjectType,
  ApiAuthUser,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import OnboardingAdPlatforms from "features/onboarding/components/OnboardingAdPlatforms";
import OnboardingLayout from "features/onboarding/components/OnboardingLayout";
import OnboardingLocations from "features/onboarding/components/OnboardingLocations";
import OnboardingPixel from "features/onboarding/components/OnboardingPixel";
import OnboardingPlatformSelection from "features/onboarding/components/OnboardingPlatformSelection";
import OnboardingScanning from "features/onboarding/components/OnboardingScanning";
import OnboardingStepActions from "features/onboarding/components/OnboardingStepActions";
import OnboardingStepBasicInfo from "features/onboarding/components/OnboardingStepBasicInfo";
import OnboardingStepBusinessInfo from "features/onboarding/components/OnboardingStepBusinessInfo";
import {
  getOnboardingPanelParagraphs,
  onboardingStepsMap,
  standardOnboardingStepNumber,
  STANDARD_ONBOARDING_TOTAL_STEPS,
} from "features/onboarding/utils";
import { Dispatch, SetStateAction } from "react";
import { AnimatePresence, motion } from "motion/react";

interface IStandardOnboardingFlowProps {
  step: ApiOnboardingStep;
  project: ApiProject;
  client: ApiClient;
  onBack: () => void;
  onNext: (inviteUser?: boolean) => void;
  learningShown: boolean;
  onLearningShow: () => void;
  settingByType: (key: ApiProjectSettingKey) => ApiProjectSetting | undefined;
  setSettings: Dispatch<SetStateAction<ApiProjectSetting[]>>;
  hasProjectType: (type: ApiProjectType) => boolean;
  hasPermission: (role: ApiACLRole) => boolean;
  inactiveUsers: ApiAuthUser[];
  onStepChange: (
    nextStep: ApiOnboardingStep,
    confirmedStep?: ApiOnboardingStep,
  ) => void;
}

function StandardOnboardingFlow({
  step,
  project,
  client,
  onBack,
  onNext,
  learningShown,
  onLearningShow,
  settingByType,
  setSettings,
  hasProjectType,
  hasPermission,
  inactiveUsers,
  onStepChange,
}: IStandardOnboardingFlowProps) {
  const stepMeta = onboardingStepsMap[step];
  const stepNumber = standardOnboardingStepNumber[step] ?? 1;
  const isConnections = step === ApiOnboardingStep.CONNECTIONS;
  const isSuperAdmin = hasPermission(ApiACLRole.SUPER_ADMIN);

  const panelParagraphs = getOnboardingPanelParagraphs(stepMeta);

  const nextLabel = isConnections
    ? isSuperAdmin
      ? "Approve"
      : "Finish"
    : "Next";

  const renderStepContent = () => {
    switch (step) {
      case ApiOnboardingStep.BASIC_INFO:
        return <OnboardingStepBasicInfo project={project} client={client} />;
      case ApiOnboardingStep.BUSINESS_INFO:
        if (!learningShown) {
          return (
            <OnboardingScanning project={project} onShown={onLearningShow} />
          );
        }
        return (
          <OnboardingStepBusinessInfo
            project={project}
            projectSetting={
              settingByType(ApiProjectSettingKey.PROJECT_TYPE) as ApiProjectSetting
            }
            setProjectSettings={setSettings}
          />
        );
      case ApiOnboardingStep.PLATFORM:
        return <OnboardingPlatformSelection project={project} />;
      case ApiOnboardingStep.PIXEL:
        return <OnboardingPixel project={project} />;
      case ApiOnboardingStep.LOCATIONS:
        return (
          <OnboardingLocations
            project={project}
            onComplete={
              !hasProjectType(ApiProjectType.TYPE_ECOMMERCE) ||
              project.platform !== ApiPlatform.PLATFORM_SHOPIFY
                ? () =>
                    onStepChange(
                      ApiOnboardingStep.CONNECTIONS,
                      ApiOnboardingStep.LOCATIONS,
                    )
                : undefined
            }
            nextDisabled={
              !hasProjectType(ApiProjectType.TYPE_ECOMMERCE) ||
              project.platform !== ApiPlatform.PLATFORM_SHOPIFY
            }
          />
        );
      case ApiOnboardingStep.CONNECTIONS:
        return (
          <OnboardingAdPlatforms
            onContinue={() => onNext()}
            continueLabel={nextLabel}
            onContinueWithInvite={
              isSuperAdmin ? () => onNext(true) : undefined
            }
            inactiveUsers={inactiveUsers}
          />
        );
      default:
        return null;
    }
  };

  return (
    <OnboardingLayout
      stepNumber={stepNumber}
      totalSteps={STANDARD_ONBOARDING_TOTAL_STEPS}
      panel={{
        heading: stepMeta?.title ?? "",
        headingAccent: stepMeta?.titleAccent,
        paragraphs: panelParagraphs,
      }}
      onBack={onBack}
      showBack={step !== ApiOnboardingStep.BASIC_INFO}
    >
      <Stack
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              style={{ height: "100%" }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </Box>
        {!isConnections && (
          <OnboardingStepActions
            onNext={() => onNext()}
            nextLabel={nextLabel}
          />
        )}
      </Stack>
    </OnboardingLayout>
  );
}

export default StandardOnboardingFlow;
